import { DateColumn, DateRange, MatrixCell, MatrixRow, ReportViewType } from '../types/reports';

/**
 * Extended MatrixRow with unique key based on hierarchy path.
 * This allows the same entity (e.g., user) to appear under multiple parents
 * with independent expand/collapse state.
 */
export interface FlattenedMatrixRow extends MatrixRow {
  uniqueKey: string;
}

/**
 * Flattens hierarchy to visible rows (for rendering).
 * Generates unique keys based on hierarchy path to support same entity
 * appearing under multiple parents (e.g., same user in different projects).
 */
export const flattenVisibleRows = (
  rows: MatrixRow[],
  expandedState: Record<string, boolean>
): FlattenedMatrixRow[] => {
  const result: FlattenedMatrixRow[] = [];

  const traverse = (items: MatrixRow[], parentPath: string = '') => {
    for (const row of items) {
      // Generate unique key based on hierarchy path
      const uniqueKey = parentPath ? `${parentPath}/${row.id}` : row.id;

      result.push({ ...row, uniqueKey });

      // Use uniqueKey for expanded state lookup (allows independent expand/collapse per parent)
      const isExpanded = expandedState[uniqueKey] ?? row.isExpanded ?? true;
      if (row.children && row.children.length > 0 && isExpanded) {
        traverse(row.children, uniqueKey);
      }
    }
  };

  traverse(rows);
  return result;
};

/**
 * Formats seconds to compact display string (e.g., "2:30")
 */
export const formatTimeForReport = (seconds: number): string => {
  if (seconds === 0) return '';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  } else {
    return `0:${minutes.toString().padStart(2, '0')}`;
  }
};

/**
 * Calculates the total number of visible columns (for CSS grid)
 */
export const calculateColumnCount = (dateRange: DateRange): number => {
  const start = new Date(dateRange.startDate);
  const end = new Date(dateRange.endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays + 1;
};

// =========================================
// View Aggregation Types and Functions
// =========================================

/**
 * Aggregated column for weekly/monthly views
 */
export interface AggregatedColumn {
  key: string;
  label: string;
  subLabel?: string;
  dates: string[];
  isWeekend?: boolean;
}

/**
 * Aggregated cell data
 */
export interface AggregatedCell {
  key: string;
  totalSeconds: number;
  logCount: number;
}

/**
 * Get ISO week number for a date
 */
const getWeekNumber = (date: Date): number => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

/**
 * Aggregate daily columns into weekly columns
 */
export const aggregateColumnsWeekly = (columns: DateColumn[]): AggregatedColumn[] => {
  const weekMap = new Map<string, AggregatedColumn>();

  columns.forEach((col) => {
    const date = new Date(col.date);
    const year = date.getFullYear();
    const week = getWeekNumber(date);
    const key = `${year}-W${week.toString().padStart(2, '0')}`;

    if (!weekMap.has(key)) {
      weekMap.set(key, {
        key,
        label: `W${week}`,
        subLabel: `${year}`,
        dates: [],
      });
    }
    weekMap.get(key)!.dates.push(col.date);
  });

  return Array.from(weekMap.values());
};

/**
 * Aggregate daily columns into monthly columns
 */
export const aggregateColumnsMonthly = (columns: DateColumn[]): AggregatedColumn[] => {
  const monthMap = new Map<string, AggregatedColumn>();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  columns.forEach((col) => {
    const date = new Date(col.date);
    const year = date.getFullYear();
    const month = date.getMonth();
    const key = `${year}-${(month + 1).toString().padStart(2, '0')}`;

    if (!monthMap.has(key)) {
      monthMap.set(key, {
        key,
        label: monthNames[month],
        subLabel: `${year}`,
        dates: [],
      });
    }
    monthMap.get(key)!.dates.push(col.date);
  });

  return Array.from(monthMap.values());
};

/**
 * Aggregate daily cells based on aggregated columns
 */
export const aggregateCells = (
  cells: MatrixCell[],
  aggregatedColumns: AggregatedColumn[]
): AggregatedCell[] => {
  const cellMap = new Map<string, MatrixCell>();
  cells.forEach((cell) => cellMap.set(cell.date, cell));

  return aggregatedColumns.map((col) => {
    let totalSeconds = 0;
    let logCount = 0;

    col.dates.forEach((date) => {
      const cell = cellMap.get(date);
      if (cell) {
        totalSeconds += cell.totalSeconds;
        logCount += cell.logCount;
      }
    });

    return {
      key: col.key,
      totalSeconds,
      logCount,
    };
  });
};

/**
 * Get aggregated columns based on view type
 */
export const getAggregatedColumns = (
  columns: DateColumn[],
  viewType: ReportViewType
): AggregatedColumn[] | null => {
  switch (viewType) {
    case 'weekly':
      return aggregateColumnsWeekly(columns);
    case 'monthly':
      return aggregateColumnsMonthly(columns);
    default:
      return null;
  }
};

/**
 * Calculate column count for CSS grid based on view type
 */
export const calculateColumnCountForView = (
  dateRange: DateRange,
  viewType: ReportViewType,
  columns: DateColumn[]
): number => {
  switch (viewType) {
    case 'weekly': {
      const aggregated = aggregateColumnsWeekly(columns);
      return aggregated.length;
    }
    case 'monthly': {
      const aggregated = aggregateColumnsMonthly(columns);
      return aggregated.length;
    }
    default:
      return calculateColumnCount(dateRange);
  }
};

/**
 * Get cell width based on view type
 */
export const getCellWidthForView = (viewType: ReportViewType): number => {
  switch (viewType) {
    case 'weekly':
      return 70;
    case 'monthly':
      return 80;
    default:
      return 52;
  }
};
